import { Link, router } from '@inertiajs/react'
import { useState } from 'react'

const MILESTONE = {
  delivered:   { label: 'Delivered',  color: '#4CAF50' },
  settled:     { label: 'Settled',    color: '#4CAF50' },
  closed:      { label: 'Closed',     color: '#607D8B' },
  cancelled:   { label: 'Cancelled',  color: '#F44336' },
  session_closed: { label: 'Closed',  color: '#607D8B' },
  handoff_complete: { label: 'Delivered', color: '#4CAF50' },
}

function statusBadge(status) {
  const colors = {
    delivered:  'bg-green-100 text-green-800',
    settled:    'bg-green-100 text-green-800',
    closed:     'bg-gray-100 text-gray-600',
    cancelled:  'bg-red-100 text-red-700',
  }
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  )
}

function milestoneDot(key) {
  const m = MILESTONE[key] || { label: key?.replace(/_/g, ' ') || '—', color: '#9E9E9E' }
  return (
    <span style={{ color: m.color }} className="inline-flex items-center gap-1 text-xs font-bold">
      <span style={{ backgroundColor: m.color }} className="w-2 h-2 rounded-full inline-block" />
      {m.label}
    </span>
  )
}

async function deleteSession(id, csrf) {
  await fetch(`/admin/queue_monitor/${id}?from_history=1`, {
    method: 'DELETE',
    headers: { 'X-CSRF-Token': csrf, 'Accept': 'application/json' },
  })
  router.reload({ only: ['sessions'] })
}

export default function QueueHistory({ sessions = [], rails_env: railsEnv = 'production' }) {
  const [deleting, setDeleting] = useState(null)
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <Link href="/admin/queue_monitor" className="text-gray-400 hover:text-gray-700 text-sm">
                ← Monitor
              </Link>
              <span className="text-gray-300">/</span>
              <h1 className="text-2xl font-bold text-gray-900">Queue History</h1>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              Completed and closed sessions · {sessions.length} records
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {sessions.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <p className="text-4xl mb-3">📋</p>
            <p className="text-xl font-semibold text-gray-700">No completed sessions yet</p>
            <p className="text-gray-500 mt-1">Delivered and closed sessions will appear here.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3 font-semibold">Session</th>
                  <th className="px-4 py-3 font-semibold">Offer</th>
                  <th className="px-4 py-3 font-semibold">Final Status</th>
                  <th className="px-4 py-3 font-semibold">Last Event</th>
                  <th className="px-4 py-3 font-semibold">Mode</th>
                  <th className="px-4 py-3 font-semibold">Events</th>
                  <th className="px-4 py-3 font-semibold">Creator</th>
                  <th className="px-4 py-3 font-semibold">Guest</th>
                  <th className="px-4 py-3 font-semibold">Created</th>
                  <th className="px-4 py-3 font-semibold"></th>
                  {railsEnv === 'development' && <th className="px-4 py-3 font-semibold text-red-400">Dev</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sessions.map(s => (
                  <tr
                    key={s.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => router.visit(`/admin/queue_monitor/history/${s.id}`)}
                  >
                    <td className="px-4 py-3 font-mono text-gray-700">#{s.id}</td>
                    <td className="px-4 py-3 max-w-[180px]">
                      <p className="font-medium text-gray-800 truncate">{s.offer_name}</p>
                      <p className="text-xs text-gray-400">Offer #{s.offer_id}</p>
                    </td>
                    <td className="px-4 py-3">{statusBadge(s.session_status)}</td>
                    <td className="px-4 py-3">{milestoneDot(s.latest_event || s.session_status)}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                        {s.sensepoint_mode || 'cloud'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700 font-semibold">{s.event_count}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">
                      {s.creator_user_id ? `u${s.creator_user_id}` : '—'}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">
                      {s.guest_user_id ? `u${s.guest_user_id}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                      {s.created_at ? new Date(s.created_at).toLocaleString(undefined, {
                        month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      }) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        href={`/admin/queue_monitor/history/${s.id}`}
                        className="text-blue-600 hover:underline text-xs"
                        onClick={e => e.stopPropagation()}
                      >
                        Detail →
                      </Link>
                    </td>
                    {railsEnv === 'development' && (
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <button
                          disabled={deleting === s.id}
                          onClick={async () => {
                            if (!window.confirm(`Delete session #${s.id}? Cannot be undone.`)) return
                            setDeleting(s.id)
                            const csrf = document.querySelector('meta[name="csrf-token"]')?.content
                            await deleteSession(s.id, csrf)
                            setDeleting(null)
                          }}
                          className="text-xs text-red-500 border border-red-300 px-2 py-0.5 rounded hover:bg-red-50 disabled:opacity-40"
                        >
                          {deleting === s.id ? '…' : 'Delete'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
